import React, { createContext, useContext, useState, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { deDE } from '@mui/material/locale';

interface ThemeContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextType>({ darkMode: false, toggleDarkMode: () => {} });
export const useThemeMode = () => useContext(ThemeContext);

const getTheme = (dark: boolean) => createTheme({
  palette: {
    mode: dark ? 'dark' : 'light',
    primary: { main: '#4a7c59', light: '#7aac6b', dark: '#1c4f2f', contrastText: '#fff' },
    secondary: { main: '#d4a843', light: '#e8c76a', dark: '#a67f1a' },
    background: dark
      ? { default: '#121212', paper: '#1e1e1e' }
      : { default: '#f0f4f0', paper: '#ffffff' },
    success: { main: '#4caf50' },
    warning: { main: '#ff9800' },
    error: { main: '#d32f2f' },
    info: { main: '#2196f3' },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 700 }, h5: { fontWeight: 600 }, h6: { fontWeight: 600 },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none' as const, fontWeight: 600, borderRadius: 8 },
        containedPrimary: { boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(74,124,89,0.3)' } },
      },
    },
    MuiCard: { styleOverrides: { root: { boxShadow: dark ? '0 1px 4px rgba(0,0,0,0.3)' : '0 1px 4px rgba(0,0,0,0.08)', borderRadius: 12 } } },
    MuiTableCell: { styleOverrides: { head: { fontWeight: 700, backgroundColor: dark ? '#2a2a2a' : '#f5f7f5' } } },
    MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
  },
}, deDE);

export const ThemeProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('bw-dark-mode') === 'true');
  const toggleDarkMode = () => setDarkMode(prev => { localStorage.setItem('bw-dark-mode', String(!prev)); return !prev; });
  const theme = useMemo(() => getTheme(darkMode), [darkMode]);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
